// src/components/desk/LeadsFeed.jsx — the Leads exclusive feed, extracted VERBATIM
// from IntakeTab.jsx (DESK_MODE_REWRITE_PLAN §3 decomposition, behavior-preserving).
// Leads render through the SAME HistoryGroup card as every other Desk entry (owner
// ruling) and, since 2026-07-23, take the SAME full filter set + server sort
// (scope=leads on the unified endpoint, 0187 RPC fork).

import { useEffect, useMemo, useRef, useState } from 'react'
import { t, tOr, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { HistoryGroup, EntriesMapView } from './HistoryCards.jsx'
import { btn, COUNTER, SkeletonCard, AdTextCtx, ViewerCtx } from './primitives.jsx'
import { FeedPager } from './FeedPager.jsx'
import { getAgentListings, getAgentListingPins, getLeads, getListingThumbs, getListingDescriptions, getListingViews, voteDistrict, getAuthorRoleMarks, recordMarketReport } from '../../db/core.js'
import { fetchPrioritized } from '../../lib/thumbPriority.js'
import { multiPickClusterCut } from '../../lib/deskFeed.js'
import { readDeskCache, writeDeskCache } from '../../db/deskCache.js'
import { readQuery, writeQuery } from '../../db/queryCache.js'
import { QUERY_KEY_VERSION } from '../../lib/queryKey.js'
import { freshnessTier, deskDirtyAt } from '../../lib/deskFreshness.js'
import { PossibleMatchesShelf, possibleClusterGroups, SHELF_PAGE } from './PossibleMatches.jsx'
// B307b module-scope pin memory — see the corpusPins effect in LeadsFeed. One session,
// one fetch per period window; a page reload starts cold (the payload is too big for the
// desk cache). KEYED by `since`: the pool is period-bounded like the feed, and an unkeyed
// memory would serve last month's pins under a "7 days" pill without a hint of it.
const leadPinsMem = new Map()
import { ResultCount } from './ResultCount.jsx'
import { UnsentValuations } from './UnsentValuations.jsx'

// Leads feed (0163, 2026-07-21): crawler-discovered OWNER supply. A lead is NOT
// a matched property — it never reaches clients until an agent secures the owner
// (cooperation → trigger promotes). Claims are NON-exclusive work markers:
// several agents may chase the same owner; whoever cooperates wins. The owner
// phone is NOT in this payload (has_phone boolean only) — the number goes
// through the existing reveal endpoint, which logs every look.
// B111: outcome density on a lead card — the competition signal that is NOT
// backwards. "Opened/seen by 8 agents" reads as *too late* and suppresses effort
// on precisely the best leads; it measures attention, which is cheap. "6 called,
// 5 no answer" measures what calls PRODUCED, and it correctly reads as *still
// available, keep trying*.
//
// ⚠️ Renders NOTHING until at least one call has an outcome. A lead nobody has
// reported on is silent on purpose. The server does not even SEND reveal counts
// or distinct-agent counts (see core_leads.js) — not because they are hard, but
// because a payload that carries them is a payload someone eventually renders.
// ⚠️ Counts only, never names (RULING 5).
function LeadContactDensity({ c }) {
  if (!c?.contacts) return null
  const o = c.outcomes || {}
  // "someone already has this owner" is the one outcome that should stop an
  // agent rather than encourage them — so it wins the badge when present.
  const taken = (o.already_taken || 0) + (o.agreed || 0)
  const warm = (o.interested || 0) + (o.callback_requested || 0)
  const cls = taken ? 'bg-rose-500/15 text-rose-600'
    : warm ? 'bg-emerald-500/15 text-emerald-600'
    : 'bg-muted text-muted-foreground'
  const label = taken ? tLog('desk_lead_density_taken', { n: taken })
    : warm ? tLog('desk_lead_density_warm', { n: warm })
    : tLog('desk_lead_density_tried', { n: c.contacts, x: o.no_answer || 0 })
  return <span className={`${COUNTER} ${cls}`}
    title={tLog('desk_lead_density_hint', { n: c.contacts })}>{label}</span>
}

// Grid, IDENTICAL to the property feed's (owner ruling 2026-07-25: "make lead view similar
// to property to have consistency across"). Leads had diverged on both views at once — its
// card view was a 340px auto-fill grid where Properties is ONE `max-w-4xl` column, and its
// gallery minmax was 440px against the property feed's 340px. Same category, same controls,
// two different layouts: switching between them read as switching apps, and the card view's
// narrow columns are what forced the footer to stay stacked on a desktop that had the room.
// ⚠️ Keep this in step with IntakeTab's entries grid — it is the same ruling, expressed twice
// because the two feeds own their own containers. Change one, change the other.
const LEAD_GRID = view => view === 'gallery'
  ? '[grid-template-columns:repeat(auto-fill,minmax(min(340px,100%),1fr))]'
  : 'grid-cols-1 max-w-4xl'

// ⚠️ DEBOUNCE, not a cooldown (0178). A cooldown would say "the owner should not
// be contacted"; this says "the same lead should not be re-grabbed in the same
// breath". ~60 minutes. The rename is the point — three times this session a
// name has been the actual bug.
const cooling = l => !!l?.debounce_until && new Date(l.debounce_until) > new Date()

// Does this lead's strip have anything to say? The strip draws a hairline, so it must not
// render when every badge inside it is absent — an empty ruled row reads as a broken card.
// It used to always have content because the ContactPill lived in it; that pill moved to the
// footer rail (2026-07-25), so the emptiness became reachable. ⚠️ Keep this in sync with the
// badges below: a new one that is not listed here simply never appears on an otherwise-quiet
// lead, and it fails silently.
const leadStrip = g => {
  const l = g?.__lead || {}
  return !!(l.claim_count > 0 || l.contact?.contacts || l.status === 'engaged' || cooling(l) || l.source_kind === 'public_evaluation'
    || l.watched_agent?.name || l.exclusive_until || l.exclusive_until_sold || l.reported_unavailable_count > 0
    || l.status === 'dead')
}

// SWR page-0 cache (owner 2026-07-24: "lead loading must be instant, like property cards").
// Property cards feel instant because IntakeTab holds their rows in memory across category
// switches; leads refetched from zero every time the category was entered. Module-level so
// it survives LeadsFeed unmounting (the whole feed unmounts on category switch) — render
// the last snapshot for this exact query signature immediately, revalidate behind it.
// Snapshots are page-0 only (cursor resets); loadMore pages live on top as before.
const leadsCache = new Map()
const LEADS_CACHE_MAX = 8
// 2026-09-03: TTL-gated re-fetch. The SWR design in the query effect refetched page 0 on
// EVERY query entry even with a snapshot in hand, so an agent re-running their own filters
// (re-entry, back/forth, a facet toggle) fired a fresh v66_slow DB hit each time — measured
// 54% of the EXPENSIVE leads searches reaching the DB were exact repeats. When a snapshot is
// younger than this, the effect paints it and SKIPS the refetch. Near-lossless: the leads
// data source itself only moves on an ~11-min cadence, so a few-minute-old cache returns
// essentially what a refetch would. Degrades safely — a miss or a stale entry refetches
// exactly as before.
// ── 2026-09-24: the fast-follow landed, and the window became the owner's ──────────────────────
// (1) PERSISTED across reloads: every snapshot is also written to the Desk's IndexedDB query cache
//     (db/queryCache.js — the store signOutCleanup.js already deletes) under `leadsDiskKey`, and a
//     memory miss hydrates from it before anything is fetched. Thumbnails are not persisted; a
//     hydrated snapshot re-requests them for the rows it paints.
// (2) The fixed 5-minute LEADS_FRESH_MS is replaced by lib/deskFreshness.js: the server-sent TTL
//     (`x-desk-cache-ttl`, 300 s fallback) decides "fresh — ask nothing"; up to 15 min it paints and
//     revalidates behind the paint; past 15 min it paints and refetches at once; and a snapshot
//     written before the agent's own last save is never fresh (a claim/release must show).
const cachePutMem = (key, snap) => {
  leadsCache.delete(key)
  leadsCache.set(key, snap)
  while (leadsCache.size > LEADS_CACHE_MAX) leadsCache.delete(leadsCache.keys().next().value)
}
const leadsDiskKey = key => `${QUERY_KEY_VERSION}|leadsnap|${key}`
const cachePut = (key, snap, ttlMs = null) => {
  snap.at = Date.now()   // freshness stamp read by the TTL gate in the query effect
  snap.ttlMs = ttlMs
  cachePutMem(key, snap)
  // rows and shelf go in as entities (one list, split by nRows on the way back); the rest is meta.
  writeQuery(leadsDiskKey(key), {
    listings: [...(snap.rows || []), ...(snap.shelf || [])], nRows: (snap.rows || []).length,
    total: snap.total, cursor: snap.cursor, leadByProp: snap.leadByProp, searchMeta: snap.searchMeta ?? null,
    shelfTotal: snap.shelfTotal ?? null, cacheTtlMs: ttlMs,
  }, { fetchedAt: snap.at }).catch(() => {})
}
// Memory miss → the persisted snapshot, rebuilt in the in-memory shape (thumbs empty). Painting
// window: the owner's 15-minute bound is the REFETCH bound, not a paint bound, so anything the
// query cache still holds (its own 6 h ceiling) may paint while the refetch runs.
async function readLeadsDisk(key) {
  const hit = await readQuery(leadsDiskKey(key)).catch(() => null)
  if (!hit || !Array.isArray(hit.listings)) return null
  const n = Number.isFinite(hit.nRows) ? hit.nRows : hit.listings.length
  const snap = { rows: hit.listings.slice(0, n), shelf: hit.listings.slice(n), total: hit.total, cursor: hit.cursor,
    leadByProp: hit.leadByProp || {}, thumbs: {}, searchMeta: hit.searchMeta ?? null, shelfTotal: hit.shelfTotal ?? null,
    at: hit.fetched_at, ttlMs: hit.cacheTtlMs ?? null, fromDisk: true }
  if (!leadsCache.has(key)) cachePutMem(key, snap)
  return leadsCache.get(key)
}
const leadsTier = snap => snap
  ? freshnessTier({ fetchedAt: snap.at, ttlMs: snap.ttlMs, dirtyAt: deskDirtyAt() })
  : 'none'

// Query signature + page-0 fetch, SHARED between the feed and prefetchLeads below —
// one implementation so the prefetch's cache key can never drift from the one the
// feed computes on entry (a drifted key = a prefetch that warms nothing, silently).
// 60 → 30, measured live 2026-08-12 on api.sentavus.com. Four districts, order balanced
// (30 first on two, 60 first on the other two, so warm buffers cannot flatter either arm):
//
//   district      limit=30                  limit=60
//   Vake          2,804 ms  206 KB   61 cards   3,098 ms  286 KB  112 cards
//   Saburtalo     2,957 ms  195 KB   35 cards   3,439 ms  264 KB   79 cards
//   Gldani        2,628 ms  180 KB   38 cards   2,889 ms  263 KB   90 cards
//   Nadzaladevi   2,758 ms  180 KB   39 cards   2,924 ms  259 KB   88 cards
//   mean          2,787 ms  190 KB   ~43        3,088 ms  268 KB   ~92
//
// −301 ms (−10%) and −29% bytes. 30 CLUSTERS is still ~43 cards, well past one screen.
//
// ⚠️ The two numbers do not move together, and that is the finding worth keeping: bytes fall
// 29% while time falls 10%, so ~2.6 s of this is FIXED cost that no page size can reach (the
// search RPC alone measures 1.1–1.3 s). Shrinking the page is worth doing and is not the fix
// for that floor — do not read the 10% as the latency work being finished.
//
// 📌 This is the LEADS page size. The properties / our-work feed has its own (LISTING_PAGE in
// IntakeTab), and there the unfiltered page deliberately stays at 100 because `localFilterMode`
// only engages when a whole feed fits in one page. Leads has no such gate — the pool is tens
// of thousands of rows and never fits — so there is nothing here for a smaller page to break.
// ── B884, 2026-08-20 — 30 → 90, measured on this very pool ──────────────────────────────
// Owner: *"filtered by price and the page kept updating for minutes, adding new findings."*
// Reproduced here: one price filter (50k–100k USD) issued FOUR sequential searches, offsets
// 0/30/60/90, because the prefetch effect below reads 3 display pages ahead (PAGE_SIZE 30)
// and each server chunk only covered one of them. On this pool a chunk is ~3 s, so the agent
// watched cards land for ~17 s — the "kept updating" he reported.
// The RPC's own cost by p_limit, warm, leads pool, that same filter:
//     30 → 1,351 ms · 90 → 1,650 ms · 300 → 2,708 ms · 1,000 → 6,045 ms   (~5 ms per row)
// Three 30-row chunks ≈ 4,050 ms and three round trips; one 90-row chunk is 1,650 ms and one
// paint. ⚠️ NOT "fetch them all", which is what was literally asked for: the cost is linear
// in rows, 1,000 is 6 s before anything appears, and this filter matches 5,549 clusters. 90 is
// exactly where the 3-page lookahead is satisfied by a single request.
// 📌 PAGE_SIZE (30) still owns what a screen shows; this is only the fetch chunk.
const PAGE = 90
// B203 (2026-07-27): the text predicate comes from the COMPILED filter set, never from the
// raw box. `query` is the whole string the agent typed; `advFilters.q` is what the search
// compiler could not claim after turning "ქირავდება"/"ბინა" into intent/kind chips. This
// used to spread advFilters and then overwrite its `q` with the raw string, so on the same
// keystroke the properties pipeline asked for `q=თბილისი` while leads asked for
// `q=ქირავდება ბინა თბილისი`. The haystack is addresses only and matching is AND-per-token,
// so the two already-claimed tokens can never match and the whole query collapses to zero
// rows — an empty leads view for a search that works fine on Properties.
//
// An empty `q` on a live compiled set is MEANINGFUL, not missing: it says the compiler
// claimed every token, so no text predicate belongs in the request at all. That is why this
// reads `advFilters.q` rather than falling back on falsiness. The raw string is used only
// when there is no compiled set to speak for it.
// ⚠️ `since` (the toolbar period pill) MUST be in the cache key, not just in the request.
// It was missing from BOTH until 2026-07-31: the pill rendered over this feed, looked
// active, and bounded nothing — every Leads query was all-time regardless of what it said
// (owner-caught). Putting it only in the request would be the second half of the same bug:
// the key would collide across periods and "Last 7 days" would paint the cached 30-day
// snapshot. Same rule the archive flag already follows — a parameter that changes the
// answer belongs in the signature.
// ⚠️ `mine` (the ownership LENS, SEARCH_GRAMMAR §3b) is in the KEY for the same reason
// `since` and `archive` are: it changes the answer. Left out, flipping Mine↔All would paint
// the other lens's cached snapshot — the exact bug the `since` note above records.
const leadsQuery = ({ query, advFilters, sort, archive, since = null, mine = false }) => {
  const rawQ = (query || '').trim()
  const q = advFilters ? (advFilters.q || '') : rawQ
  const filt = (q || advFilters || sort)
    ? { ...(advFilters || {}), ...(q ? { q } : {}), ...(sort ? { sort } : {}) }
    : undefined
  return { q, filt, since, mine, key: JSON.stringify([archive, since || null, filt || null, mine || false]) }
}
const fetchLeadsPage = async (filt, archive, offset, signal, unknownOffset = 0, since = null, mine = false) => {
  const r = await getAgentListings({
    scope: 'leads', leadStatus: archive ? 'dead' : undefined,
    since: since || undefined,
    // 0362: the lens is EVIDENCE now, not a claim. `mine` still names the prop because
    // it is the agent's own relationship to the lead; what changed is which relationship —
    // work that happened (represents_property) rather than an intention that was declared.
    workedByMe: mine,
    limit: PAGE, offset, unknownOffset, filters: filt, signal,
    // ⚠️ NOT FREE — asking for the histograms here is what chooses the SLOW IMPLEMENTATION.
    // 0687 read this as "the histograms come home for free on the response the feed already
    // makes". They do arrive on it; the RESPONSE is what costs. The search RPC's fast branch
    // requires `coalesce(p_range_facets, false) = false`, so requesting them routes page 0 away
    // from the materialised reader (~1.3 s, zero temp spill) into the slow body (10–18 s, spills)
    // — for a reason that has nothing to do with the agent's filters. Every API restart drops
    // every open Desk onto that path at once, which is the burst that crash-looped the box on
    // 2026-09-02 (B1166 / B1174).
    // Measured in the browser on this very view after the v66 deploy: page-0 leads requests
    // 1,260 / 2,309 / 3,552 ms, and the limit:1 meta warm 17,862 ms.
    //
    // The histograms come from `warmLeadSearchMeta` instead — which already exists for the bare
    // pool, already carries THE SAME filters, is keyed and cached per query, and fires on a
    // 1,200 ms delay specifically so it never races page 0 for a connection or a search slot.
    // Its own guard ("or the filtered path answered on the feed's own response") is what made it
    // no-op under a filter; it now does that work too.
    //
    // ⚠️ WHAT THIS BUYS, AND WHAT IT DOES NOT. The feed gets fast on every search. The histograms
    // still cost one slow call per NEW query key — they cannot come from the fast layer, which
    // has no price or area in it at all (that is B1166's build). So this is NOT "8x on every
    // search": it is a fast feed plus a cached background cost, where before every page-0 search
    // paid the slow body on the critical path an agent waits on.
    // ⚠️ Two DB preconditions had to land first, because this changes WHICH IMPLEMENTATION
    // answers and not merely how fast: 0824 (the fast reader's platform count was 219/47,478 off
    // cluster-wide truth) and 0825 (its source cut had no eligibility test and was member- rather
    // than cluster-scoped, worth 3.8%). Parity verified at 0 on bare/intent/sources afterwards.
    rangeFacets: false,
  })
  // Overlay for exactly this page's rows — never the whole pool. Meta is awaited (cards
  // without their lead overlay are dropped by the groups filter, so it gates first
  // paint); THUMBNAILS don't — they patch in when they land, exactly like the property
  // feed's lazy-thumbs pass (owner 2026-07-24: leads must feel instant).
  //
  // ⚠️ SHELF ROWS GO THROUGH THE SAME OVERLAY (2026-07-29). `possible_listings` is the
  // unknown-verdict tier the server has always sent and this feed always discarded — the
  // Leads category rendered no shelf at all, so under `posted since 1 July` 14,456 shelved
  // clusters were invisible AND uncounted. They render through the same lead card as any
  // other lead, which means they need the same lead metadata: a group without `__lead` is
  // dropped by the groups filter below, silently, so omitting them here would look exactly
  // like "the shelf is empty".
  const shelf = r.possibleListings || []
  const both = [...r.listings, ...shelf]
  const pids = [...new Set(both.map(l => l.property_id).filter(Boolean))]
  const lids = both.map(l => l.listing_id).filter(Boolean)
  // ⚠️ Each listing's OWN text only — origins are deliberately not fetched (owner ruling
  // 2026-08-02: "non-source chips hover shouldn't show comment"). A repost of ours has no
  // owner comment, and lending it the source ad's words would attribute them to our posting.
  // An origin fallback was briefly shipped here and reverted the same day.
  const adTextIds = lids
  const meta = pids.length ? await getLeads({ propertyIds: pids }) : []
  // Priority split (2026-08-10, owner: "load first image first, then the rest in the
  // background"): `thumbsP` used to be a started Promise resolving ONCE with the whole
  // page's photo map — every card went from no-photo to photo at the same instant, on the
  // slowest id in the batch. It is now a function callers invoke with a callback that fires
  // once per stage (a small priority batch, then the rest); every consumer below already
  // merges (`{...prev, ...th}`), so being called twice is exactly what they're built for.
  const thumbsP = onBatch => fetchPrioritized(lids, getListingThumbs, onBatch)
  // The ad's own words, for the platform-chip hover. Rides the SAME loader as thumbs
  // because this feed has its own fetch path: IntakeTab's descriptions effect skips every
  // SPECIAL_FEED (Leads included), so without this the chips on the busiest surface in the
  // Desk hover blank — which is exactly how it shipped and was caught.
  const adTextP = adTextIds.length ? getListingDescriptions(adTextIds).catch(() => ({})) : Promise.resolve({})
  // 👁 portal view counts. Same lazy side-read as thumbs/ad text, and missed for the same
  // reason they were: IntakeTab's views effect returns early on every SPECIAL_FEED, so this
  // feed got none — while its OWN panel carries a "views ≥ N" hunter lens (0294). An agent
  // could filter leads by a number their cards were structurally unable to show.
  const viewsP = pids.length ? getListingViews(pids).then(m => Object.fromEntries(m)).catch(() => ({})) : Promise.resolve({})
  return { ...r, shelf, meta, thumbsP, adTextP, viewsP }
}

// Hover-prefetch (perceived-speed remainder, 2026-07-24): warm the page-0 snapshot for
// the query the feed WILL run when the user clicks the Leads category, so cold entry
// paints from cache like a re-entry does. Fire-and-forget; the feed still revalidates
// on mount (SWR), so a prefetch can never pin stale data — it only moves the spinner
// off-screen. Deduped per key; errors are swallowed (the real load surfaces them).
const prefetching = new Set()
export async function prefetchLeads({ query, advFilters, sort, archive = false, since = null, mine = false }) {
  const { filt, key } = leadsQuery({ query, advFilters, sort, archive, since, mine })
  if (leadsCache.has(key) || prefetching.has(key)) return
  prefetching.add(key)
  try {
    // A persisted snapshot young enough to BE the answer ends the prefetch here — hydrating it
    // into memory is all the caller's re-read tick needs (2026-09-24).
    const disk = await readLeadsDisk(key)
    if (disk && leadsTier(disk) === 'fresh') return
    const r = await fetchLeadsPage(filt, archive, 0, undefined, 0, since, mine)
    const snap = {
      rows: r.listings, total: r.total, cursor: r.rowsReturned,
      leadByProp: Object.fromEntries(r.meta.map(m => [m.property_id, m])), thumbs: {},
      searchMeta: r.searchMeta,   // 0229: facet counts ride the same page-0 snapshot as total
      shelf: r.shelf, shelfTotal: r.unknownExcluded,   // the shelf rides page 0 too, or a cache hit paints a feed with no shelf
    }
    cachePut(key, snap, r.cacheTtlMs ?? null)
    r.thumbsP(th => { snap.thumbs = { ...snap.thumbs, ...th } })   // merge: thumbsP fires per stage now
  } catch { /* prefetch is best-effort */ }
  finally { prefetching.delete(key) }
}

// 0687: the corpus-wide price/area/land histograms for the search panel on the LEADS pool.
//
// The bare leads feed is served by the materialised cluster pager (0339b). That path carries the
// facet COUNTS (0230) but it can never carry these: `lead_cluster_value` stores kind, intent,
// source and district, and holds no price or area at all. Measured 2026-08-21 -- a bare leads
// request answers `rpc_version=undefined` and `range_facets` ABSENT, so the panel's three
// sliders fell back to the loaded page: bars summing to 103 rows against 30,319 clusters.
//
// ONE extra request fixes it and the hot path is not touched. A non-default sort alone trips the
// server's `filtered` gate, so `sort:'added', limit:1` routes through the search RPC and returns
// the corpus meta with a one-row page -- the same trick the Properties view already uses to warm
// its counts.
//
// ⚠️ DELIBERATELY NOT solved by teaching the materialised pager to emit them. That path is ~88%
// of leads traffic and answers in ~1.9s precisely because it never joins `listing` or
// `current_claim`; adding a price/area aggregate would buy those joins back on the one request
// that must stay cheap. A background warm costs nothing the agent waits for.
//
// Fires once per query key. On failure the key is released, or one dropped request would cost
// this session its histograms for that query forever.
// ⚠️ THE WARM MUST NOT DEPEND ON ARRIVING SECOND. The first version patched the cached snapshot
// in place (`if (cur) cur.searchMeta = ...`) and marked the key done either way. But the warm
// fires 1.2s after mount while the feed's own page 0 lands around 3s, so on a cold pool
// `leadsCache.get(key)` was still empty: the answer was fetched, thrown away, and the key was
// already spent so nothing ever retried. It worked at first purely on timing, then stopped —
// owner saw the metro facet with no numbers and the histograms had silently gone back to the
// loaded page at the same moment.
// So what the warm learns is kept HERE, keyed by the same query key, and merged on READ. Order
// stops mattering: whichever of the two arrives second, the panel sees both.
const warmInFlight = new Set()
const warmedMeta = new Map()
const WARMED_MAX = 40
// ── THE WARM MUST SURVIVE A RELOAD (crash #15, 2026-09-02 18:04:59 UTC) ──────────────────────
// `warmedMeta` is an in-process Map, so EVERY MOUNT is a cold miss by construction. Production
// consequence, measured in search_exec_log: the identical bare leads warm (scope=leads,
// sort=added, q_len=0, predicates={}) fired TEN times between 17:57:03 and 18:03:46 — every
// 30-45 s — at 7.6-8.2 s each on the slow body, and 19.6 s / 18.5 s where two overlapped. The
// box died at 18:04:59, ending a 4h57m clean run. Something re-mounts that view every half
// minute (a reload habit, a second tab, a poll); each mount bought a fresh corpus scan.
// 📌 The guard below was already correct and still let this happen, because "cached" was true
// within a page's life and false across page lives — and nobody had asked which one mattered.
// A per-process cache in front of an expensive query is a cache against repetition, not against
// repetition BY A USER, and a user reloading is the normal case.
const WARM_TTL_MS = 10 * 60 * 1000   // the layer it reads refreshes ~every 11 min; a fresher stamp buys nothing
const warmDiskKey = k => 'lw:' + k
function warmFromDisk(key) {
  const hit = readDeskCache(warmDiskKey(key))
  if (!hit || Date.now() - (hit.at || 0) > WARM_TTL_MS) return null
  return hit.carry || null
}
export async function warmLeadSearchMeta({ query, advFilters, sort, archive = false, since = null, mine = false }) {
  const { filt, key } = leadsQuery({ query, advFilters, sort, archive, since, mine })
  if (warmInFlight.has(key)) return false
  // Already learned for this exact query, or the filtered path answered on the feed's own
  // response -- either way, do not buy a second corpus scan.
  const done = warmedMeta.get(key)
  if (done?.range_facets && done?.metro_counts) return false
  const have = leadsCache.get(key)?.searchMeta
  if (have?.range_facets && have?.metro_counts) return false
  // ⚠️ ACROSS RELOADS TOO. Hydrate from the disk cache and answer from it — the point is not only
  // to skip the fetch but to still HAVE the histograms, so a reload is not a blank panel.
  const disk = warmFromDisk(key)
  if (disk?.range_facets && disk?.metro_counts) {
    warmedMeta.set(key, { ...(warmedMeta.get(key) || {}), ...disk })
    const cur0 = leadsCache.get(key)
    if (cur0) cur0.searchMeta = { ...(cur0.searchMeta || {}), ...disk }
    return true
  }
  warmInFlight.add(key)
  try {
    const r = await getAgentListings({
      scope: 'leads', leadStatus: archive ? 'dead' : undefined,
      since: since || undefined, workedByMe: mine,
      // `sort` is top-level on purpose: it overrides whatever the feed's own sort was, and the
      // override is harmless because a histogram does not care what order the page came in.
      limit: 1, offset: 0, sort: 'added', filters: filt, rangeFacets: true,
    })
    // ⚠️ NAME every key carried over, never spread the whole answer. The materialised pager
    // already emits kind/intent/source/district for this same query, and a blanket merge would
    // let this warm's copy silently outrank the feed's own. Same discipline as the server's
    // searchMeta whitelist -- a key nobody names does not travel.
    //
    // metro_counts is here because the pager CANNOT emit it: `lead_cluster_value` stores
    // kind/intent/source/district and holds no metro link, so on the bare Leads pool the metro
    // facet rendered its station names with no numbers at all (owner-reported 2026-08-22, and
    // confirmed live: geo.metroCounts was NULL while the search path returned 22 stations).
    // The request was already being made and its metro tally thrown away.
    const meta = r.searchMeta || {}
    const carry = {}
    if (meta.range_facets) carry.range_facets = meta.range_facets
    if (meta.metro_counts) carry.metro_counts = meta.metro_counts
    if (!Object.keys(carry).length) return false
    // Record it against the query key FIRST -- this is what makes the warm independent of who
    // arrives first. Patching the snapshot too is a convenience for readers that hold it already.
    warmedMeta.set(key, { ...(warmedMeta.get(key) || {}), ...carry })
    while (warmedMeta.size > WARMED_MAX) warmedMeta.delete(warmedMeta.keys().next().value)
    // Persist so the NEXT mount does not buy this scan again — the whole point of the fix above.
    // Best-effort: deskCache refuses outsized entries and can throw on a full quota, and a failure
    // here must degrade to today's behaviour (one warm per mount) rather than lose the answer we
    // already have in memory.
    try { writeDeskCache(warmDiskKey(key), { at: Date.now(), carry }) } catch { /* memory copy stands */ }
    const cur = leadsCache.get(key)
    if (cur) cur.searchMeta = { ...(cur.searchMeta || {}), ...carry }
    return true
  } catch {
    return false
  } finally {
    warmInFlight.delete(key)
  }
}

// The category tree's Leads count (owner 2026-07-24: "leads category is missing count
// numbers"). Read off the SAME page-0 snapshot the feed renders, keyed by the SAME
// signature — so the number in the rail is BY CONSTRUCTION the number the feed will show.
// A second count query would be a second source that can disagree with the feed the moment
// filters differ, which is exactly the "silently wrong derived field" shape this codebase
// keeps getting bitten by.
// ⚠️ Returns null (render nothing) when THIS query has not been fetched yet, rather than
// the total from a different filter set — a stale number here reads as "there are 12 leads
// matching your search" and would be a lie. Warmed on Desk mount + the existing hover
// prefetch; entering the category fills it for free.
export function leadsCachedTotal({ query, advFilters, sort, archive = false, since = null, mine = false }) {
  return leadsCache.get(leadsQuery({ query, advFilters, sort, archive, since, mine }).key)?.total ?? null
}

// 0229 (B205): the sidebar facets' numbers for the LEADS view, read off the same page-0
// snapshot as leadsCachedTotal — same signature, same key, same null-when-not-fetched rule,
// for the same reason: a count from a different filter set is a lie with digits in it.
export function leadsCachedSearchMeta({ query, advFilters, sort, archive = false, since = null, mine = false }) {
  const key = leadsQuery({ query, advFilters, sort, archive, since, mine }).key
  const snap = leadsCache.get(key)
  if (!snap) return null            // rule 3 -- not fetched yet, say nothing
  // Merge anything warmLeadSearchMeta learned for THIS key. The materialised pager cannot emit
  // metro_counts or range_facets at all, so without this they are simply absent on the bare pool.
  const warm = warmedMeta.get(key)
  return warm ? { ...(snap.searchMeta || {}), ...warm } : (snap.searchMeta ?? null)
}

// The page-0 ROWS, for the search panel's own arithmetic — the price/area histograms and the
// quick-select buckets, which are computed from the loaded distribution rather than from
// anything written down (lib/rangePresets.js).
//
// 🚨 WHY THIS EXISTS. The panel is handed `listings={listingEntries}`, and `listingEntries` is
// the OUR-WORK feed — "Leads has its own feed", as the comment on leadsCachedTotal says. So on
// the Leads pool the panel received an EMPTY array and every number it derives fell back:
// `niceCeiling` returned its hardcoded 1,000,000 fallback (it needs 5 values), the histogram drew
// two bars, and the quick-select chips came out as a generic `< $200K · $200K–$500K · $500K+`
// regardless of what the agent was actually searching. Owner-reported on a
// იყიდება · ბინა · თბილისი search, where the honest buckets are ~85K/130K.
// ⚠️ Same key, same signature and the same null-when-not-fetched rule as the two readers above,
// and for the same reason: rows from a different filter set would put a histogram under this
// search that belongs to another one.
export function leadsCachedRows({ query, advFilters, sort, archive = false, since = null, mine = false }) {
  return leadsCache.get(leadsQuery({ query, advFilters, sort, archive, since, mine }).key)?.rows ?? null
}

// ⚠️ favorites/onToggleFavorite must be FORWARDED to HistoryGroup or the ♥ silently
// vanishes on lead cards: HistoryItem renders the heart only when BOTH props are
// truthy (`onToggleFavorite && favorites`), so omitting them is not a no-op with a
// default — it opts the whole feed out with no error. Leads were the ONLY feed
// missing them (found 2026-07-24, user-caught); the main feeds pass both.
// The heart is a PRIVATE bookmark and deliberately says nothing to colleagues —
// "an agent is working this" is a claim-level signal, not an interest-level one.
export function LeadsFeed({ agentId, query, advFilters, sort, since = null, districts, features, onCardClick, archive = false, onArchiveChange = () => {}, onSnapshot = null, view = 'card', favorites = null, onToggleFavorite = null, archived: archivedSet = null, onToggleArchive = null, page: pageProp = null, onPage = null,
  // ⚠️ FORWARD ALL OF THESE TO HistoryGroup OR THE FEED HAS NO ACTIONS. HistoryItem gates
  // its whole action row on `onOpenCooperation || onAddMeeting || onShare || onOpenTerms`
  // being truthy, with no warning when they are absent — the ♥ favorites trap again
  // (LEADS_AND_DISCOVERY.md). Since 0205 these ARE the evidence that marks a lead
  // `working`, so a lead card without them cannot be worked at all.
  // ⚠️ clearByListing was PASSED by IntakeTab and never destructured here until 2026-07-31 —
  // it hit the floor, so every lead card rendered a CLEARED restriction as still standing.
  // Third instance of this exact trap on this component (♥ favorites, then the action row).
  // sharesByProperty rides the same rails, and the same warning applies: this feed's rows
  // report their ids up (onVisibleIds) so the ONE loader in IntakeTab fills it. Forwarding
  // the prop without that would render every lead as "no links sent".
  coopsByProperty = null, coopOffersByProperty = null, sharesByProperty = null, restrByListing = null, termsByListing = null, clearByListing = null, onOpenCooperation = null,
  // 0894: the card-face distinctiveness note, loaded by IntakeTab's ONE loader off the ids this
  // feed reports up (onVisibleIds) — the same rails as sharesByProperty, same warning: forwarded
  // here AND populated there, or every lead renders without its note and nothing says why.
  distinctByProperty = null,
  // developer-card ranges (core 1663) — same rails as distinctByProperty: forwarded here AND populated in IntakeTab.
  devCardsByProperty = null,
  sellersByProperty = null,
  // `mine` — the ownership LENS (SEARCH_GRAMMAR §3b). NOT a stage and NOT a category: the
  // inventory is still Leads and the whole sentence underneath is unchanged; this only asks
  // "the ones I hold a live claim on". Threaded into the cache key, page 0, load-more AND
  // the shelf, because a lens that reaches some of those and not others is the half-existing
  // -key trap this file has already been bitten by three times.
  mine = false, onMineChange = null,
  // ⚠️ THE MAPS ABOVE ARRIVE EMPTY WITHOUT THIS (2026-08-02). This feed owns its rows, so
  // their ids never appear in the parent's pageGroups — the coop/restriction fetches there
  // simply never asked about them, and every lead card rendered the 🤝＋ "add" affordance and
  // no 🚫 chip as though those were FACTS. Fourth instance of the same trap on this component
  // (♥ favorites → the action row → clearByListing → now the data behind them): forwarding a
  // prop is not the same as the prop being populated. Reporting ids up keeps ONE loader
  // instead of giving this feed a second copy of the stance-precedence merge.
  onVisibleIds = null,
  onAddMeeting = null, onShare = null, onRepost = null, onOpenTerms = null,
  // B307b: a corpus-pin click opens the listing card by bare id — there is no loaded
  // group to hand to onCardClick, which is the whole point of a corpus pin.
  onPinClick = null,
  // Map expand — the parent's state, because hiding the chrome is the parent's job.
  // See the comment at the old local declaration below for why this cannot live here.
  mapExpanded = false, onToggleMapExpand = null }) {
  // ── Whole-pool map pins (B307b, owner 2026-08-05: "can we do same with leads pins?") ──
  // One request brings every active lead's coordinate — 29,303 pins / ~1.8 MB measured —
  // so the leads map draws the pool, not the loaded pages. IN MEMORY only, deliberately:
  // writeDeskCache's quota-evict-retry would trade every other desk cache for one 1.8 MB
  // blob. Module-scoped, so reopening the map within a session costs nothing.
  // Unfiltered pool only: under a query/filter/archive/mine lens the corpus would
  // contradict the answer on screen (same rule as the browse map's corpusPins).
  const [corpusPins, setCorpusPins] = useState(null)
  // B719 (owner, 2026-08-15). This effect used to bail on ANY lens — `if (!bare) return` — for
  // the right reason (a corpus overlay behind a filtered answer says the filter did nothing) and
  // with the wrong consequence: nothing replaced it, so the filtered map fell back to `groups`,
  // the rows the feed had fetched, PAGE_SIZE 30 × 3 prefetched pages = 90 pins under a counter
  // reading 273. Now a filtered view asks for the filtered pins instead of asking for nothing.
  //
  // ⚠️ THIS IS THE SECOND COPY OF THAT EFFECT AND I FIXED THE OTHER ONE FIRST. IntakeTab owns
  // the browse map's `corpusPins`; this component owns the leads map's, and they share only a
  // comment. The owner was on the leads view, so the first fix changed nothing they could see
  // and the deploy looked broken. Before declaring this kind of bug fixed, grep for the state
  // name — a surface that renders in two feeds usually has two of everything.
  //
  // `archive` still bails: it is a different POOL (dead leads), and the pins RPC's leads branch
  // answers for active statuses.
  // `mine` NO LONGER BAILS (owner 2026-09-17: "we already know that anyway"). It did because the
  // pins route 400'd on the lens rather than resolve the viewer a second time — but the route
  // shares `resolveViewer` with the feed, so it now resolves it the same way and the map sends
  // the same `worked=me` the feed sends (`workedByMe: mine`, as at every getAgentListings call
  // in this file). Against an older server the 400 lands in the catch and the map draws the
  // loaded rows, exactly as before.
  useEffect(() => {
    if (view !== 'map' || archive) { setCorpusPins(null); return }
    // ⚠️ `leadsQuery` — the SAME builder the feed's own fetch uses (`filt` at the page load
    // below), not `advFilters` raw. It is what folds the text query in as `q`, so a map built
    // from the raw props would answer a different question than the counter above it. Its
    // `key` is already the feed's cache signature, which makes it exactly the right memo key.
    const { filt, key: qKey } = leadsQuery({ query, advFilters, sort, archive, since, mine })
    // `mine` is in the key on its own: it is a lens, not a filter, so `filt` can be null under it
    // and two different answers would otherwise share the unfiltered key.
    const memKey = `${since || 'all'}:${mine ? 'mine' : ''}:${filt ? qKey : ''}`
    if (leadPinsMem.has(memKey)) { setCorpusPins(leadPinsMem.get(memKey)); return }
    // 🔴 2026-09-17, the same defect fixed in IntakeTab the same day (this IS the second copy the
    // note above warns about): the previous question's pins stayed on the map until the new
    // answer arrived, and forever when the request failed. Pins must belong to THIS key or be
    // absent — absent falls back to the feed's own server-filtered rows.
    setCorpusPins(null)
    let dead = false
    getAgentListingPins({ scope: 'leads', since, workedByMe: mine, ...(filt ? { filters: filt } : {}) })
      .then(r => {
        if (dead || !Array.isArray(r?.pins)) return
        leadPinsMem.set(memKey, r.pins)
        setCorpusPins(r.pins)
      })
      .catch(e => { if (!dead) console.warn('[leads map] pins request failed — drawing the loaded rows instead', e?.status || e?.message || e) })   // overlay — a failed fetch leaves the loaded-row map
    return () => { dead = true }
    // `sort` rides along because leadsQuery folds it into `filt`. It does not change WHICH
    // pins match — the route ignores order — so this only costs a re-fetch on a sort change,
    // which is the cheaper half of the trade against a stale closure reading last sort's filt.
  }, [view, query, advFilters, sort, archive, mine, since])

  // 0187 scope=leads unification (2026-07-23, owner: "exactly the same search"): listing
  // CONTENT comes from the SAME /api/core/agent-listings pipeline as the property category
  // (server-side normalized text search, cluster pagination, shapeListingRow cards) — the
  // old getLeads full-fetch + client substring filter is gone, so the feed no longer caps
  // at the newest 100 and "vake" matches Georgian addresses. getLeads survives as the
  // lead-METADATA overlay (status/claims/contact density), fetched per loaded page by
  // property id — the 🤝 coop-chips pattern.
  const [rows, setRows] = useState(null)         // shapeListingRow rows, cluster_key attached
  const [total, setTotal] = useState(0)          // cluster (property) count, server truth
  const [cursor, setCursor] = useState(0)        // offset in cluster units (rows_returned)
  // B1198. WHICH OFFSET rows[0] CAME FROM. It was always 0 -- the feed could only ever hold a
  // prefix of the result set, so page 5 of 1,035 was unreachable and the pager could only offer
  // what had been walked to. The feed now holds a WINDOW that may start anywhere, so a jump is
  // one request instead of a walk.
  // 🔑 Measured before building it, because the assumption ran the other way: a deep offset is
  // CHEAPER here, not dearer -- offset 0 costs 1,515-2,049 ms and offset 30,000 costs 711-1,060,
  // reproduced in both directions so it is not a warming curve. `p_offset > 0` skips the
  // total-count computation, so every page except the first avoids the expensive half.
  const [rowsOffset, setRowsOffset] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)
  const [leadByProp, setLeadByProp] = useState({})   // property_id → property_lead overlay
  const [thumbs, setThumbs] = useState({})       // listing_id → [urls]
  const [adTexts, setAdTexts] = useState({})     // listing_id → { text, at } (ad body, redacted server-side)
  // 0374 — property_id → { yes, no, roster }. Counts only; author phones never ride a
  // batch response. Soft failure: no marks means plain 📞 pills, never "nobody is marked".
  const [authorMarks, setAuthorMarks] = useState(new Map())
  const viewerCtx = useMemo(() => ({ agentId }), [agentId])
  const [views, setViews] = useState({})         // property_id → view counts (👁 chip)
  // ── Possible-matches shelf (2026-07-29) ─────────────────────────────────────────────
  // The unknown-verdict tier, which this feed silently dropped until now. Its own cursor,
  // in CLUSTER units — see PossibleMatches.jsx for why that unit is load-bearing.
  const [shelfRows, setShelfRows] = useState([])
  const [shelfTotal, setShelfTotal] = useState(null)   // whole-corpus count, null = unknown
  const [shelfOffset, setShelfOffset] = useState(0)
  const [shelfLoading, setShelfLoading] = useState(false)
  const shelfDoneRef = useRef(false)     // an empty page means spent, even if the count says more
  const shelfBusyRef = useRef(false)     // state can't close the double-click window
  const [err, setErr] = useState(null)
  // ⚠️ Map expand is the PARENT's state, not ours (owner-reported 2026-08-05: "enlarger not
  // working" on the Leads map). It was local here — declared for "Leads view parity" in
  // 2026-07-24 — and local is the one thing it cannot be: enlarging the map means HIDING THE
  // PAGE CHROME, and the facet rail and the ranking aside are rendered by IntakeTab, gated on
  // ITS `mapExpanded`. So this button flipped its own ⤢/⤡ glyph and nothing else moved, which
  // reads exactly like a dead button. The state is now lifted; IntakeTab's own reset (leaving
  // map view drops expanded) covers this feed too, since both read the same `view`.
  // General shape of the bug, worth recognising elsewhere: a control whose EFFECT lives in an
  // ancestor cannot own its state, and a prop that is merely rendered back (`expanded`) makes
  // it look wired.
  // B115 v3 / RULING C: archive is a DESTINATION, not a deletion — dead leads stay
  // reachable behind one toggle. The unified path serves it via lead_status=dead, and the
  // server deliberately does NOT require an active listing there (a sold lead must render).
  // CONTROLLED since 2026-07-24 (lifted to IntakeTab, owner: "these can go in დეტალური
  // ძიება"): the panel's Category→Leads subtree is the surface — same contract as
  // MeetingsFeed's statusFilter. The old above-the-grid pill row is gone.
  // Owner ruling 2026-07-23: "lead category is treated like properties — same filter
  // search bar." The FULL advanced-filter set (price/rooms/district/facets, the same
  // effAdvFilters object the property category sends) + server sort now apply here,
  // not just the text query — the 0187 leads fork of the search RPC accepts the whole
  // v13 body, so every panel filter and future filter works on leads automatically.
  // One key per distinct server query — identifies both the cache snapshot and the
  // in-flight request (stale-response guard: a slow revalidate must never clobber a
  // newer query's rows). Computation shared with prefetchLeads (leadsQuery above).
  const { q, filt, key: cacheKey } = leadsQuery({ query, advFilters, sort, archive, since, mine })
  const reqKeyRef = useRef(cacheKey)
  // ── Chain guards, ported VERBATIM in spirit from the property feed's loadMoreListings
  // (owner 2026-07-25: "we have well set up property card loading — implement exactly the
  // same things for leads"). Leads had only the `loadingMore` STATE flag, i.e. the feed as
  // it stood BEFORE the property feed's own 2026-07-11/07-15 fixes, and inherited both bugs:
  //
  //   loadMoreBusyRef  — `loadingMore` is state, so two prefetch-effect runs landing in the
  //     same commit window both read it as false and fire the SAME offset twice. A ref flips
  //     synchronously at call entry and closes that window; the state flag stays for the UI.
  //   chainExhaustedRef — a chunk returning 0 rows means the server has nothing more at this
  //     cursor even when `cursor < total` (total drifts from what paging yields). Without it
  //     the effect re-fires the same empty offset forever — an unbounded request storm from
  //     one scroll position, on the exact endpoint whose saturation takes the Desk down.
  //   loadFailsRef — 3 strikes stop the auto-chain (a failing fetch otherwise retries in a
  //     storm, because the effect retriggers every time `loadingMore` flips back to false).
  //     The manual retry still works and a success resets the count.
  //
  // ⚠️ All three reset together in the query effect — a new query is a new chain.
  const loadMoreBusyRef = useRef(false)
  const chainExhaustedRef = useRef(false)
  const loadFailsRef = useRef(0)
  //   lastAskRef — B884: the `query:offset` pair this chain has ALREADY requested. The three
  //     refs above do not cover it, and the gap is measured: on the leads pool one price
  //     filter fetched offset 60 TWICE (2,877 ms, then 79 ms). `cursor` is state, so a second
  //     prefetch tick can arrive AFTER the first request resolved and cleared
  //     `loadMoreBusyRef` but BEFORE `setCursor` rendered — both ticks then compute the same
  //     offset and both ask for it. Keyed by `cacheKey` as well as the offset so it needs no
  //     reset when the query changes: a new query is a new key and can never collide with the
  //     old one's asks. Cleared on failure, so a retry is never mistaken for a duplicate.
  const lastAskRef = useRef(null)
  //   rowsGenRef — review 2026-09-23: WHICH ROW WINDOW a page belongs to. `reqKeyRef` compares
  //     keys, so it cannot tell an old request for key A from a new one after A→B→A: a late
  //     load-more from the first A was appended after the second A's page 0 and left a gap.
  //     Bumped by the query effect (new chain) and by a landed jump (new window); a load-more
  //     or jump reply whose generation is no longer current is dropped.
  const rowsGenRef = useRef(0)
  //   page0KeyRef — WHICH QUERY THE CURSOR BELONGS TO. The three refs above guard against
  //     firing the same page twice; this one guards against firing a page of a query whose
  //     page 0 has not landed, which is a different bug and was live.
  //
  // 🚨 MEASURED IN THE BROWSER (2026-08-14, one district checkbox click on the leads feed):
  //     1,526 ms  limit=30 offset=30   ← page 2, of a query with no page 1
  //     2,237 ms  limit=30 offset=0    ← page 1, 700 ms LATER
  //     3,511 ms  limit=30 offset=60
  //     5,615 ms  limit=30 offset=60   ← byte-identical duplicate
  //   Four full lead-corpus scans for one click, two of them junk, ~4 s of the wall clock
  //   the agent waits for street counts.
  //
  //   The prefetch effect's deps are [clampedPage, groups.length, loadingMore, cursor,
  //   total] — NOT the filters. A filter change moves `groups.length`, so the effect fires
  //   against a cursor belonging to the PREVIOUS snapshot while `filt` is already the new
  //   one. `reqKeyRef` cannot catch it: by then reqKeyRef is already the new key, so the
  //   stale-cursor rows are ACCEPTED, cursor advances to 60, page 0 lands and resets it to
  //   30, and the chain asks for 60 a second time. That is the duplicate.
  //
  // ⚠️ FAILS TOWARD ONE MISSED PREFETCH, NEVER A STRANDED CHAIN — deliberately. If this ref
  //   is ever wrong, loadMore() skips once and the next scroll re-triggers the same effect.
  //   The opposite design (gate the effect on `rows`) can wedge pagination at page 1, which
  //   is far worse than the waste it replaces.
  const page0KeyRef = useRef(null)
  const [loadFailed, setLoadFailed] = useState(false)
  // Superseded page-0 requests are ABORTED, not just dropped-on-arrival (2026-07-27).
  // The reqKey guard already kept a stale response from painting, but the request itself
  // kept RUNNING a full lead-corpus scan server-side — three filter clicks in one second
  // were three concurrent 18k-row scans, which is exactly the concurrency that pushed the
  // search RPC into the DB's 8s statement timeout and 500ed the whole feed (the 0221
  // migration fixed the per-query cost; this fixes the self-inflicted multiplier).
  const abortRef = useRef(null)
  // ── LATEST-WINS, ONE PAGE-0 REQUEST AT A TIME (2026-10-07) ─────────────────────────────
  // 🔴 The abort above stops the BROWSER waiting; it does not stop the work. The API server and
  // the search RPC run every request they received to the end. Measured in search_exec_log on
  // 2026-10-06 08:09:18-08:10:00: an agent unticking districts one by one (99, 98, 96 ... 37)
  // sent 42 leads searches in 42 seconds, each 15-28 s, all running at once on the database.
  // The 350 ms debounce cannot help -- the clicks were ~1 s apart.
  // So while a page-0 request is in flight, a new query does NOT start another: it is
  // remembered, and when the in-flight one lands the LATEST query runs once. A burst of N
  // clicks now costs at most 2 searches instead of N. The in-flight request is no longer
  // aborted on a query change (its answer is simply dropped by the reqKeyRef guard, as before);
  // it is still aborted on unmount.
  const inflightRef = useRef(false)
  const rerunRef = useRef(false)
  const loadRef = useRef(null)
  const firstLoadRef = useRef(true)   // first load of the feed's life fires undebounced
  const fetchPage = (offset, signal) => fetchLeadsPage(filt, archive, offset, signal, 0, since, mine)
  async function load() {
    if (inflightRef.current) { rerunRef.current = true; return }
    inflightRef.current = true
    const key = cacheKey
    const ctrl = new AbortController()
    abortRef.current = ctrl
    try {
      const r = await fetchPage(0, ctrl.signal)
      if (reqKeyRef.current !== key) return   // user already switched query — drop
      const leadMap = Object.fromEntries(r.meta.map(m => [m.property_id, m]))
      setRows(r.listings); setTotal(r.total); setCursor(r.rowsReturned); setRowsOffset(0)
      // The cursor now belongs to `key`, so paging is allowed again. Set from `key`, never
      // from `cacheKey`: this closure can outlive its own query, and stamping the CURRENT
      // key here would re-authorise paging for a question this response did not answer.
      page0KeyRef.current = key
      setLeadByProp(leadMap)
      // A new query is a new shelf — reset its cursor with the page-0 answer it belongs to.
      setShelfRows(r.shelf); setShelfTotal(r.unknownExcluded ?? null)
      setShelfOffset(0); shelfDoneRef.current = false; shelfBusyRef.current = false
      setErr(null)   // a stale claim-error banner must not outlive a successful reload
      loadFailsRef.current = 0; setLoadFailed(false)
      chainExhaustedRef.current = false
      // review 2026-09-23: the shelf rides the snapshot exactly as prefetchLeads stores it — omitted
      // here, a re-entry inside LEADS_FRESH_MS painted the feed with no shelf and no shelf count.
      const snap = { rows: r.listings, total: r.total, cursor: r.rowsReturned, leadByProp: leadMap, thumbs: {}, searchMeta: r.searchMeta,
                     shelf: r.shelf, shelfTotal: r.unknownExcluded ?? null }
      cachePut(key, snap, r.cacheTtlMs ?? null)
      // The feed's OWN page-0 landing must announce itself (0229, fixing a PRE-EXISTING
      // race that also blanked the rail's Active-leads total under any filtered query):
      // the parent's cache re-read tick was only ever bumped by the mount/hover/debounce
      // PREFETCHES, and on a filter change the fast archive prefetch resolves before this
      // slower filtered fetch — so the last tick fired before this snapshot existed and
      // the rail/facets read null forever. Same callback idiom as onArchiveChange.
      onSnapshot?.()
      r.thumbsP(th => {
        snap.thumbs = { ...snap.thumbs, ...th }   // enrich the stored snapshot too, so a cache hit replays photos
        if (reqKeyRef.current === key) setThumbs(prev => ({ ...prev, ...th }))
      })
      r.adTextP.then(ad => { if (reqKeyRef.current === key) setAdTexts(prev => ({ ...prev, ...ad })) })
      r.viewsP?.then(vw => { if (reqKeyRef.current === key) setViews(prev => ({ ...prev, ...vw })) })
    // ⚠️ A page-0 failure is a TRANSPORT failure, not a message worth quoting. `fetch`
    // rejects with the browser's own wording — Safari says literally "Load failed", which
    // is what the owner's phone showed above the cards (2026-07-25): a permanent red banner
    // saying nothing actionable, with no way to retry short of changing the query, while
    // any cached rows kept rendering underneath it. It reads as a data error; it is a
    // dropped request. Now it flips a flag the UI renders as one quiet retry line, and the
    // rows already on screen stay — the property feed's posture (console + a manual button),
    // not a shout. `err` stays reserved for CLAIM failures, which are real, specific and
    // worth quoting verbatim.
    } catch (e) {
      // An abort is a SUPERSEDED request (newer query, or unmount), never a transport
      // failure — counting it toward the 3-strike banner would show "load failed" for
      // typing fast.
      if (e?.name === 'AbortError') return
      if (reqKeyRef.current !== key) return
      loadFailsRef.current++
      setLoadFailed(true)
      console.error('[leads load]', e.message)
    } finally {
      inflightRef.current = false
      if (abortRef.current === ctrl) abortRef.current = null
      // A query arrived while this one ran: run the LATEST one now (loadRef holds the newest
      // render's closure). Skipped when the query on screen is the one that just landed.
      if (rerunRef.current) {
        rerunRef.current = false
        if (reqKeyRef.current !== key) loadRef.current?.()
      }
    }
  }
  loadRef.current = load
  async function loadMore() {
    if (loadMoreBusyRef.current || chainExhaustedRef.current) return
    // The cursor must belong to THIS query. Without this the effect above happily asks for
    // offset=30 of a query whose page 0 is still 700 ms away — see page0KeyRef's note.
    if (page0KeyRef.current !== cacheKey) return
    // B884 — never ask for the same (query, offset) twice. See lastAskRef's note.
    const askKey = `${cacheKey}::${cursor}`
    if (lastAskRef.current === askKey) return
    lastAskRef.current = askKey
    loadMoreBusyRef.current = true
    const key = cacheKey
    const gen = rowsGenRef.current   // review 2026-09-23
    setLoadingMore(true)
    try {
      const r = await fetchPage(cursor)
      if (reqKeyRef.current !== key || rowsGenRef.current !== gen) return
      if (r.rowsReturned === 0) chainExhaustedRef.current = true
      loadFailsRef.current = 0
      setRows(prev => {
        const seen = new Set((prev || []).map(l => l.listing_id))
        return [...(prev || []), ...r.listings.filter(l => !seen.has(l.listing_id))]
      })
      setTotal(r.total); setCursor(c => c + r.rowsReturned)
      setLeadByProp(prev => ({ ...prev, ...Object.fromEntries(r.meta.map(m => [m.property_id, m])) }))
      r.thumbsP(th => { if (reqKeyRef.current === key) setThumbs(prev => ({ ...prev, ...th })) })
      r.adTextP.then(ad => { if (reqKeyRef.current === key) setAdTexts(prev => ({ ...prev, ...ad })) })
      r.viewsP?.then(vw => { if (reqKeyRef.current === key) setViews(prev => ({ ...prev, ...vw })) })
    } catch (e) {
      if (reqKeyRef.current === key) { loadFailsRef.current++; console.error('[leads load-more]', e.message) }
      // B884: release the duplicate guard on FAILURE, or the retry `loadFailsRef` allows
      // would be read as the duplicate it is designed to block and the chain would stop at
      // the first network blip.
      lastAskRef.current = null
    }
    finally { loadMoreBusyRef.current = false; setLoadingMore(false) }
  }
  // Next page of the shelf. Its own fetch, because the shelf is a second result set with
  // its own ordering (v17's shelf rank), not a continuation of the matched page. Only the
  // shelf half of the response is consumed — the rest is page 0 again, and merging it would
  // reset the matched chain's cursor.
  async function loadMoreShelf() {
    if (shelfBusyRef.current || shelfDoneRef.current) return
    shelfBusyRef.current = true
    const key = cacheKey
    const next = shelfOffset + SHELF_PAGE
    setShelfLoading(true)
    try {
      // limit: 1 — we want the shelf, not another matched page.
      // ⚠️ `since` belongs here too: this is the one call that does not go through
      // fetchLeadsPage, so omitting it would page the shelf over an ALL-TIME pool while
      // page 0 was bounded by the period — more shelf rows than the tally that labels them.
      const r = await getAgentListings({
        scope: 'leads', leadStatus: archive ? 'dead' : undefined,
        since: since || undefined,
        workedByMe: mine,   // the shelf must obey the same lens as page 0
        limit: 1, offset: 0, unknownOffset: next, filters: filt,
      })
      if (reqKeyRef.current !== key) return
      const shelf = r.possibleListings || []
      if (!shelf.length) { shelfDoneRef.current = true; return }
      // Same overlay rule as page 0: no `__lead`, no card.
      const pids = [...new Set(shelf.map(l => l.property_id).filter(Boolean))]
      const meta = pids.length ? await getLeads({ propertyIds: pids }) : []
      if (reqKeyRef.current !== key) return
      setShelfOffset(next)
      setLeadByProp(prev => ({ ...prev, ...Object.fromEntries(meta.map(m => [m.property_id, m])) }))
      setShelfRows(prev => {
        const seen = new Set(prev.map(l => l.listing_id))
        return [...prev, ...shelf.filter(l => !seen.has(l.listing_id))]
      })
      const lids = shelf.map(l => l.listing_id).filter(Boolean)
      if (lids.length) getListingThumbs(lids).then(th => { if (reqKeyRef.current === key) setThumbs(p => ({ ...p, ...th })) }).catch(() => {})
      if (lids.length) getListingDescriptions(lids).then(ad => { if (reqKeyRef.current === key) setAdTexts(p => ({ ...p, ...ad })) }).catch(() => {})
    } catch (e) { console.error('[leads shelf-page]', e.message) }
    finally { shelfBusyRef.current = false; setShelfLoading(false) }
  }
  // Debounced on the search box — the query executes SERVER-side (same normalization the
  // property category uses), so each keystroke pause re-fetches page 0. SWR: when this
  // exact query has a snapshot, paint it NOW and let load() replace it quietly — entering
  // the category twice must not show "loading" twice.
  useEffect(() => {
    reqKeyRef.current = cacheKey
    rowsGenRef.current++   // review 2026-09-23: a new chain — in-flight load-more/jump replies are stale
    setPage(0)   // a new query is a new page-1, same as the main feed's reset effect
    // A new query is a NEW chain: the old one's exhaustion, failures and in-flight marker
    // say nothing about this one, and carrying them over would strand the new query.
    chainExhaustedRef.current = false
    loadFailsRef.current = 0
    loadMoreBusyRef.current = false
    setLoadFailed(false)
    const paintSnap = (hit) => {
      setRows(hit.rows); setTotal(hit.total); setCursor(hit.cursor); setRowsOffset(0)
      setLeadByProp(hit.leadByProp); setThumbs(prev => ({ ...prev, ...hit.thumbs })); setErr(null)
      setShelfRows(hit.shelf || []); setShelfTotal(hit.shelfTotal ?? null)
      // A snapshot carries its OWN cursor, so pagination may legitimately continue from it
      // before load() re-lands — that is the whole point of the SWR paint. The cursor and
      // the query agree here, which is exactly what the guard asks.
      page0KeyRef.current = cacheKey
    }
    // 2026-09-03: TTL-GATED SKIP. A FRESH snapshot (lib/deskFreshness.js since 2026-09-24: the
    // server-sent TTL, 300 s fallback, and not older than the agent's own last save) is
    // authoritative for its lifetime — it was painted, so here we simply do NOT refetch. This is
    // the DB-load cut: it stops re-running the agent's own repeated filters against the 3s
    // v66_slow path. Abort any page-0 request still in flight from a prior query (saves that DB
    // work too), announce the snapshot so the rail/facets read its counts exactly as load() would
    // on completion, and mark firstLoad consumed. A STALE hit or a miss falls through to the SWR
    // refetch, unchanged — so the worst case of any misjudgement is the previous behaviour.
    const freshLand = (hit) => {
      // 2026-10-07: the fresh snapshot answers this query, so a queued re-run is not needed. The
      // in-flight request (if any) is left to finish and is dropped by the reqKeyRef guard.
      rerunRef.current = false
      firstLoadRef.current = false
      onSnapshot?.()
      // review 2026-09-23: the snapshot carries rows/thumbs but not the ad text or the view
      // counts (both are lazy side-reads load() attaches), and this path skips load() — so a
      // re-entry within the window drew blank chip hovers and no 👁 counts. Re-request both
      // for exactly the rows just painted, guarded by the same key check as load().
      // 2026-09-24: …and THUMBNAILS for a snapshot hydrated from disk, which persists no photos.
      const key = cacheKey
      const both = [...(hit.rows || []), ...(hit.shelf || [])]
      const lids = both.map(l => l.listing_id).filter(Boolean)
      const pids = [...new Set(both.map(l => l.property_id).filter(Boolean))]
      if (lids.length) getListingDescriptions(lids)
        .then(ad => { if (reqKeyRef.current === key) setAdTexts(prev => ({ ...prev, ...ad })) }).catch(() => {})
      if (pids.length) getListingViews(pids)
        .then(m => { if (reqKeyRef.current === key) setViews(prev => ({ ...prev, ...Object.fromEntries(m) })) }).catch(() => {})
      const noPhoto = both.filter(l => l.listing_id && !(l.images?.length) && !hit.thumbs?.[l.listing_id]).map(l => l.listing_id)
      if (noPhoto.length) fetchPrioritized(noPhoto, getListingThumbs, th => {
        hit.thumbs = { ...(hit.thumbs || {}), ...th }
        if (reqKeyRef.current === key) setThumbs(prev => ({ ...prev, ...th }))
      }).catch(() => {})
    }
    // Debounce EVERY query change, not just typing (2026-07-27). A checkbox/facet click
    // used to fire at 0ms — clicking three filters in one second launched three full
    // corpus scans server-side (each a candidate for the DB's 8s statement wall under
    // ingest load). Only the very first load of the feed's life goes out immediately;
    // the SWR snapshot means a re-entry paints from cache during the 350ms anyway.
    // 2026-09-24: a snapshot past the owner's 15-minute bound ('expired') also goes at once —
    // "paint it but refetch immediately".
    let id = null, dead = false
    const schedule = (tier) => {
      if (dead) return
      id = setTimeout(load, (firstLoadRef.current || tier === 'expired') ? 0 : 350)
      firstLoadRef.current = false
    }
    const decide = (hit) => {
      const tier = leadsTier(hit)
      if (hit && tier === 'fresh') { freshLand(hit); return }
      schedule(tier)
    }
    const memHit = leadsCache.get(cacheKey)
    if (memHit) {
      paintSnap(memHit)
      setShelfOffset(0); shelfDoneRef.current = false; shelfBusyRef.current = false
      decide(memHit)
    } else {
      setRows(null)
      // No snapshot → the cursor on screen belongs to the PREVIOUS query. Nothing may page
      // until load() replaces it. This single line is what stops the offset=30-before-
      // offset=0 scan.
      page0KeyRef.current = null
      // ⚠️ Clear the shelf on a MISS too. Leaving the previous query's shelf on screen
      // under a new query's spinner would attach 14,000 "possible matches" to a question
      // nobody asked — the stale-number failure this feed's own count comment warns about.
      setShelfRows([]); setShelfTotal(null)
      setShelfOffset(0); shelfDoneRef.current = false; shelfBusyRef.current = false
      // 2026-09-24: a reload is no longer a cold miss — the persisted snapshot (IndexedDB, a
      // few ms) paints first and decides whether page 0 is asked for at all.
      readLeadsDisk(cacheKey).then(disk => {
        if (dead || reqKeyRef.current !== cacheKey) return
        if (disk) { paintSnap(disk); onSnapshot?.() }
        decide(disk)
      }, () => { if (!dead) schedule('none') })
    }
    // 2026-10-07: no abort here -- a query change must not free the slot for a parallel search
    // (see inflightRef). The unmount effect below still aborts.
    return () => { dead = true; clearTimeout(id) }
    // advFilters is the parent's memoized effAdvFilters (stable identity per change);
    // sort re-pages the same snapshot server-side, so both re-fetch page 0.
    // `since` is in here for the same reason it is in the cache key: without it the period
    // pill changes the request that WOULD be sent and never sends one.
    // ⚠️ `mine` belongs here for EXACTLY the reason `since` does, and it was missing on the
    // first pass: the lens changed the cache key and the request that load() WOULD send, but
    // nothing re-ran load, so page 0 kept the other lens's rows and total. Live symptom:
    // "Working" pressed, `claimed=me` on the wire from load-more, and 22,286 still on screen.
  }, [archive, q, advFilters, sort, since, mine])   // eslint-disable-line react-hooks/exhaustive-deps
  // Unmount only: stop waiting on a page-0 request nobody will read (2026-10-07, see inflightRef).
  useEffect(() => () => { abortRef.current?.abort(); rerunRef.current = false }, [])

  // Leads render through the EXACT SAME card component as every other Desk entry
  // (HistoryGroup) and open the EXACT SAME modal (handleCardClick -> ListingLineageModal).
  // 2026-07-21 user ruling: a lead is a normal property card, not a bespoke design.
  // Grouping key = the server's authoritative cluster_key (0133 identity clusters) — the
  // same unit the feed paginates by, so a group is complete the moment its page arrives.
  // Reported-gone leads sink rather than disappear (owner ruling: "visibility must
  // lower, not archive"). Sorting, not filtering.
  // 2026-10-22: two deals / two types reach the server as neither (scalar param) — cut here, see multiPickClusterCut.
  const multiCut = useMemo(() => multiPickClusterCut(advFilters), [advFilters])
  const groups = useMemo(() => {
    const byCluster = new Map()
    for (const l of rows || []) {
      const key = l.cluster_key || l.property_id || l.listing_id
      if (!byCluster.has(key)) byCluster.set(key, [])
      byCluster.get(key).push(l)
    }
    return [...byCluster.entries()].map(([key, members]) => {
      const lead = members.map(m => leadByProp[m.property_id]).find(Boolean) || null
      // ⚠️ ORIGIN FIRST — items[0] is the card's face AND what handleCardClick opens
      // (owner-caught 2026-07-25). Members arrive newest-first, and on any lead one of
      // our agents has reshared, the newest listing is OURS: the card then printed our
      // own agent as "განც. ავტორი" and the modal resolved its representative banner to
      // them (server.js keys that off the opened listing's author), so the owner looked
      // withheld when they were simply not the listing being shown.
      // Measured: 50 lead properties carried a senTavus-authored listing; ALL 50 also had
      // an outside-authored one AND reshare lineage — every case was this ordering, none
      // was a bad lead. Do NOT "fix" it by gating the pool; that deletes real owner leads.
      // Stable sort, so recency still orders within each side.
      const ordered = [...members].sort((a, b) => (a.is_reshare ? 1 : 0) - (b.is_reshare ? 1 : 0))
      const items = ordered.map(li => ({
        id: `listing:${li.listing_id}`,
        // ⚠️ NOT a hardcoded 'scraped_listing_readonly' (owner-caught 2026-07-30 on a real
        // card: an apartment whose myhome ad is the OWNER's and whose ss + korter ads are
        // Luka's reposts rendered all THREE platform chips with the dashed "scraped origin"
        // border — so our own reposts looked like untouched owner ads, and the dedup looked
        // broken when it was in fact correct).
        // The two kinds mean different things (see OBJECT_TYPE in HistoryCards): a reshare
        // has an outbound derived_from edge and is the agent's own repost; a plain capture
        // is an observed ad. The main feed has always made this distinction on exactly this
        // flag (IntakeTab's listing entries); this feed is the one that lost it — and the
        // sort directly above already reads is_reshare, so the fact was in hand.
        proposal_kind: li.is_reshare ? 'listing_readonly' : 'scraped_listing_readonly',
        status: li.status, created_at: li.listed_at || null,
        lead_badge: archive ? 'archive' : 'active',   // LEAD card badge (designer board)
        listing: { ...li, images: (li.images?.length ? li.images : thumbs[li.listing_id]) || [] },
        proposed: {}, result: {},
      }))
      return { key: `lead:${key}`, items, __lead: lead }
    }).filter(g => g.items.length && g.__lead && (!multiCut || multiCut(g.items.map(i => i.listing))))
      .sort((a, b) => (a.__lead.reported_unavailable_count || 0) - (b.__lead.reported_unavailable_count || 0))
  }, [rows, leadByProp, thumbs, multiCut])
  // Shelf cards. Grouping is the SHARED implementation (PossibleMatches.jsx) so the leads
  // shelf and the property shelf can never count differently; what is added here is what
  // makes a lead card a lead card — the overlay and the lazy thumbs. Deliberately NOT
  // re-sorted: the server already ranked this tier (owners who have answered before, first),
  // and re-ordering it client-side would discard the ranking the pager pages through.
  const shelfGroups = useMemo(() => possibleClusterGroups(shelfRows).map(g => ({
    ...g,
    items: g.items.map(it => ({
      ...it,
      listing: { ...it.listing, images: (it.listing.images?.length ? it.listing.images : thumbs[it.listing.listing_id]) || [] },
    })),
    __lead: g.items.map(it => leadByProp[it.listing.property_id]).find(Boolean) || null,
  })).filter(g => g.__lead && (!multiCut || multiCut(g.items.map(i => i.listing)))), [shelfRows, leadByProp, thumbs, multiCut])
  // Tell the parent which properties/listings this feed is rendering, so the ONE cooperation
  // and restriction loader covers them (see the onVisibleIds prop note above). Reported from
  // rows + shelfRows rather than from the paged slice: both halves render through the same
  // card, the parent chunks its own requests, and re-reporting on every page turn would churn
  // the fetch for no gain. The parent compares a joined string, so an unchanged set is a
  // genuine no-op — this must never become a render loop (see the Leads reload incident).
  useEffect(() => {
    if (!onVisibleIds) return
    const all = [...(rows || []), ...(shelfRows || [])]
    onVisibleIds(all.map(l => l.property_id), all.map(l => l.listing_id))
  }, [rows, shelfRows, onVisibleIds])
  // 👁 hidden (0375) leaves MY leads feed and nobody else's. Filtered on the GROUPS, after
  // clustering — a cluster is what a card is, so hiding one must remove the whole card and
  // not just one of its postings. Same client-held Set as ♥, so the server query is
  // identical for every agent and the pager keeps paging the same corpus.
  const groupsVisible = useMemo(
    () => (archivedSet?.size ? groups.filter(g => !archivedSet.has(g.items?.[0]?.listing?.property_id)) : groups),
    [groups, archivedSet])
  const leads = rows   // loading/empty states below key on null/length, same as before
  // Page switcher — the property feed's EXACT system (owner 2026-07-24: "same exact
  // system"): 30 groups per client page, Prev/Next pager; the server chunk (PAGE=90, and this
  // comment said 60 while the constant said 30 until B884 — it is now one chunk per lookahead)
  // prefetches ahead when the reader nears the end of what's loaded.
  const PAGE_SIZE = 30   // = the main feed's ENTRY_PAGE_SIZE
  // ⚠️ THE PAGE IS THE HOST'S WHEN THE HOST OFFERS ONE (2026-09-02). It was local, which is
  // why clicking a page number here changed the feed and not the URL: IntakeTab owns the hash
  // and could not see this number. Controlled when `page`/`onPage` are passed, local otherwise,
  // so nothing that renders this feed without them has to change.
  const [pageLocal, setPageLocal] = useState(0)
  const page = pageProp != null ? pageProp : pageLocal
  const setPage = onPage || setPageLocal
  // B1198. THE SERVER'S TOTAL, not the length of what happens to be loaded. The old form was
  // correct arithmetic on the wrong denominator: 100 loaded rows over PAGE_SIZE 30 printed
  // `1 2 3 4 +` while 3,792 results existed, and the + was the only hint that page 5 was not
  // merely missing but unreachable. `total` is already server truth in cluster units, which is
  // the unit `groups` counts in.
  // ⚠️ Falls back to the loaded length when `total` has not arrived, so a cold feed still pages
  // over what it has rather than collapsing to one page.
  // ⚠️ NEVER FEWER PAGES THAN THE ROWS IN HAND. `total` is learned from page 0 only -- a deep
  // page skips the count (that is what makes it cheap) -- so landing straight on page 756 from
  // a URL leaves it unknown. Taking the max of both means the unknown case degrades to exactly
  // the old behaviour (page over what is loaded) instead of collapsing to 1 and making the
  // pager RETURN NULL, which is what happened live: clicking the last page made the pager
  // itself disappear and left the agent with no way back.
  const totalPages = Math.max(1,
    Math.ceil(groupsVisible.length / PAGE_SIZE),
    total > 0 ? Math.ceil(total / PAGE_SIZE) : 0)
  const clampedPage = Math.min(page, totalPages - 1)
  // Where the loaded window sits in that page space, and where the requested page sits inside
  // the window. `localPage < 0` or past the end means the rows are simply not here yet.
  const windowFirstPage = Math.floor(rowsOffset / PAGE_SIZE)
  const localPage = clampedPage - windowFirstPage
  const windowPages = Math.ceil(groupsVisible.length / PAGE_SIZE)
  const pageInWindow = localPage >= 0 && localPage < windowPages
  const shown = pageInWindow
    ? groupsVisible.slice(localPage * PAGE_SIZE, (localPage + 1) * PAGE_SIZE)
    : []
  useEffect(() => {
    // 🚨 B1198: ONLY LOOK AHEAD FROM INSIDE THE WINDOW. This effect asks for one more chunk
    // whenever the reader nears the end of what is loaded -- and `totalPages` now counts the
    // whole result set, so without this gate clicking page 400 would satisfy its condition
    // forever and walk the entire corpus one chunk at a time. That is the unbounded request
    // storm this file already records twice (B884, chainExhaustedRef): a jump is served by
    // jumpToPage below, in ONE request, and the lookahead stays what it was -- a convenience
    // for reading forward.
    if (!pageInWindow) return
    if (loadingMore || loadMoreBusyRef.current || !rows || cursor >= total) return
    // Same three-part gate the property feed's prefetch effect uses. Without the exhausted
    // and fail checks this effect is the storm's engine: it retriggers on every
    // `loadingMore` false-edge, so a chunk that returns nothing (or keeps failing) is
    // re-requested at the same offset indefinitely.
    if (chainExhaustedRef.current || loadFailsRef.current >= 3) return
    if ((localPage + 3) * PAGE_SIZE > groups.length) loadMore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clampedPage, localPage, pageInWindow, groups.length, loadingMore, cursor, total])

  // ── B1198: a page outside the window is fetched directly, in ONE request ──────────────────
  // Same guards as loadMore, and for the same reasons it lists: the cursor must belong to THIS
  // query (page0KeyRef), the same (query, offset) is never asked twice (lastAskRef), one fetch
  // at a time (loadMoreBusyRef), and a late reply for a superseded query is dropped (reqKeyRef).
  // ⚠️ It REPLACES rows rather than appending: the window moves, it does not grow. Appending a
  // far page onto a prefix would leave `groups` claiming a contiguity it does not have, and
  // every slice after it would show the wrong rows.
  const jumpBusyRef = useRef(false)
  useEffect(() => {
    if (pageInWindow || !rows || loadingMore || jumpBusyRef.current || loadMoreBusyRef.current) return
    if (page0KeyRef.current !== cacheKey) return
    const offset = clampedPage * PAGE_SIZE
    const askKey = `${cacheKey}::jump::${offset}`
    if (lastAskRef.current === askKey) return
    lastAskRef.current = askKey
    jumpBusyRef.current = true
    const key = cacheKey
    const gen = rowsGenRef.current   // review 2026-09-23
    setLoadingMore(true)
    ;(async () => {
      try {
        const r = await fetchPage(offset)
        if (reqKeyRef.current !== key || rowsGenRef.current !== gen) return
        // The window moves: any load-more still in flight belongs to the OLD window.
        rowsGenRef.current++
        setRows(r.listings)
        setRowsOffset(offset)
        setCursor(offset + r.rowsReturned)
        // ⚠️ DO NOT setTotal HERE, and the reason is the optimisation this whole feature rests
        // on: `p_offset > 0` SKIPS the total-count computation -- that is precisely why a deep
        // page costs ~750 ms while page 0 costs ~1,500 ms. So a jump's reply carries no real
        // total, and writing it back collapsed totalPages to 1, which made FeedPager return
        // null: clicking the last page made the pager itself disappear.
        // Only page 0 knows how many there are. Caught by rendering, in the browser, on a real
        // jump to page 756 of 756 -- no test could have: the mistake is a correct-looking
        // assignment whose value is only wrong on the path that skips computing it.
        // A jump lands in the middle of the set, so the chain is not exhausted any more and the
        // failure counter starts fresh -- both are statements about the PREVIOUS window.
        chainExhaustedRef.current = false
        loadFailsRef.current = 0
        setLeadByProp(prev => ({ ...prev, ...Object.fromEntries(r.meta.map(m => [m.property_id, m])) }))
        r.thumbsP(th => { if (reqKeyRef.current === key) setThumbs(prev => ({ ...prev, ...th })) })
        r.adTextP.then(ad => { if (reqKeyRef.current === key) setAdTexts(prev => ({ ...prev, ...ad })) })
        r.viewsP?.then(vw => { if (reqKeyRef.current === key) setViews(prev => ({ ...prev, ...vw })) })
      } catch (e) {
        if (reqKeyRef.current === key) { loadFailsRef.current++; console.error('[leads jump]', e.message) }
        lastAskRef.current = null   // same release-on-failure rule as loadMore (B884)
      } finally { jumpBusyRef.current = false; setLoadingMore(false) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInWindow, clampedPage, cacheKey, rows, loadingMore])

  // 0374 — which of the visible leads have an author already reported as an agent.
  // Keyed on the property-id SET, not on `groups` identity: every refetch/merge produces a
  // new array even when the same properties come back, and this POST is only worth making
  // when the set actually changes (the same trap the Desk's dormancy key documents).
  const authorMarkKey = useMemo(
    () => [...new Set(groups.map(g => g.items?.[0]?.listing?.property_id).filter(Boolean))].sort().join(','),
    [groups])
  useEffect(() => {
    const pids = authorMarkKey ? authorMarkKey.split(',') : []
    if (!pids.length) { setAuthorMarks(new Map()); return }
    let gone = false
    getAuthorRoleMarks(pids).then(m => { if (!gone) setAuthorMarks(m) }).catch(() => {})
    return () => { gone = true }
  }, [authorMarkKey])

  // SOLD / RENTED on a lead card (owner 2026-10-11: "add this button rented sold on leads category too … make sure
  // marked listings don't flood current feed … it must be counted in recommended sort"). The SAME report as the
  // browse feed's button (ShelfToggles → record_market_report, reason deal_done_by_other). The server already does the
  // rest: the report raises property_lead.reported_unavailable_count, and refresh_lead_actionability_v1 classes the lead
  // 'reported_gone' → unactionable, so Best Match ranks it after every actionable lead at its next refresh.
  // ⚠️ `goneLocal` is what makes the tap visible NOW: the row's own count only changes on the next fetch, and without
  // the local count the card would keep its place and show no badge until then — which reads as "the button did nothing".
  const [goneLocal, setGoneLocal] = useState(() => new Map())
  const goneOf = g => Math.max(g?.__lead?.reported_unavailable_count || 0, goneLocal.get(g?.items?.[0]?.listing?.property_id) || 0)
  // reason 'owner_declined_agent' = the card's „არ თანამშ." - recorded, but it is no "sold" badge
  const reportGone = (l, reason = 'deal_done_by_other') => {
    const pid = l?.property_id
    if (!pid) return
    recordMarketReport(pid, { reason, agentId })
      .then(r => reason === 'deal_done_by_other' && setGoneLocal(prev => new Map(prev).set(pid, r?.report_count ?? 1)))
      .catch(e => window.alert(`${t('desk_gone_failed')}: ${e.message}`))
  }

  // ONE lead card, used by BOTH grids — the matched feed and the possible-matches
  // shelf (2026-07-29). A shelf lead is an ordinary lead whose data could not answer
  // the filter; it is not a lesser card, and giving it a stripped-down copy would be a
  // second card implementation to keep in step with this one.
  const renderLeadCard = g => (
  <div key={g.key} className="relative">
    {/* Lead-only controls ride INSIDE the card via the skeleton's strip slot
        (A→C increment 4) — the card owns the slot position, this feed supplies the
        content: phone state, claims, contact density, lifecycle badges, Claim. */}
    <HistoryGroup group={g} districts={districts} features={features}
      onCardClick={onCardClick} expanded={false} shade={0}
      /* onToggle={null}: this feed has no stack expansion, so the count chip renders
         as plain text instead of a button that does nothing (see HistoryCards). */
      onToggle={null}
      gallerySize={view === 'gallery' ? 'large' : 'thumb'}
      favorites={favorites} onToggleFavorite={onToggleFavorite}
      onReportGone={reportGone} reportedGone={goneOf(g)}
      /* 👁 hide-from-my-feed (0375). Held OFF this surface on 2026-08-02 while the shelf
         was still LABELLED "Archive", because `archive` here means the company-wide lead
         lifecycle (status='dead') and two Archives on one screen read as one thing. The
         shelf is called "Hidden" now, so the collision is gone and the noisiest feed in
         the product — 23,031 leads — gets the control it most needed. The two remain
         completely separate: this hides a card from YOU, `archive` above moves the lead
         for EVERYONE. */
      archived={archivedSet} onToggleArchive={onToggleArchive}
      authorMark={authorMarks.get(g.items?.[0]?.listing?.property_id) || null}
      coopsByProperty={coopsByProperty} coopOffersByProperty={coopOffersByProperty} sharesByProperty={sharesByProperty} restrByListing={restrByListing} termsByListing={termsByListing}
      clearByListing={clearByListing} viewsByProperty={views} distinctByProperty={distinctByProperty} devCardsByProperty={devCardsByProperty} sellersByProperty={sellersByProperty}
      onOpenCooperation={onOpenCooperation} onAddMeeting={onAddMeeting}
      onShare={onShare} onRepost={onRepost} onOpenTerms={onOpenTerms}
      /* B274: the hunting feed is the ONLY surface that asks the district question on the
         card face. Passing this is what makes the chip render at all — every other
         HistoryGroup caller omits it and is unchanged. Supplying it here rather than
         threading agentId keeps the card ignorant of who is logged in, like every other
         card callback.
         ⚠️ B277 (owner ruling 2026-08-02): NO LONGER GATED ON agentId. It used to be, because
         the endpoint demanded one — so an admin saw no question at all, including the owner on
         his own login. Managers and admins may settle a district now; the server resolves them
         through the party bridged to the verified session. Re-adding a gate here would silently
         hide the question from exactly the people who asked for it. */
      onDistrictVote={(propertyId, choice) => voteDistrict(propertyId, choice, agentId)}
      /* Phone state is the FOOTER RAIL pill's ring — solid = a number is on file,
         dashed = none yet (owner ruling 2026-07-25 mobile review: "we don't need
         ნომერი აღებულია", then "no separate dotted pill when contact is missing").
         ⚠️ ONE contact affordance per card. This strip used to carry its own
         ContactPill, which sat one row above the rail's — two phone buttons doing
         the same thing, and on a lead with nothing else recorded the strip was a
         hairline row holding a single dotted phone. The FACT lives here (the lead
         payload's has_phone boolean); the CONTROL lives in the rail, so it is passed
         down rather than rendered twice.
         ⚠️ The pill resolves numbers through getPropertyContacts, the SAME
         server-gated path the property card uses (ownership rule + via_agent
         substitution live there) — the lead payload still carries only the boolean,
         and nothing here reveals a number the server wouldn't hand this viewer on a
         property card. */
      contactKnown={!!g.__lead.has_phone}
      /* ⚠️ NO hairline and NO top margin any more (owner 2026-08-04: "this extra space is
         useless"). The card hands this to the footer as `startSlot`, so it rides the rail's
         existing row; a separator of its own would re-open the band it was moved out of. */
      strip={leadStrip(g) && <div className="flex flex-wrap items-center gap-2">
      {/* ⚠️ REMOVED 2026-08-04 on the owner's explicit second instruction ("remove these two
          totally"): the claim count ("N აგენტი მუშაობს") and the engaged badge
          ("მოლაპარაკებაშია — N"). Both were still rendering after the card gained its state
          column and its chain panel, and the owner judged them noise on the face.
          What they said, so nobody re-derives it wrongly: claim_count = agents who CLAIMED
          this lead and are calling the owner (not the same set as the agents who reposted the
          ad — the chain shows those); 'engaged' = a cooperation exists on the property and is
          not yet `agreed` (B109), i.e. someone is already in conversation with the owner.
          ⚠️ B109's ENFORCEMENT is untouched — the server still 409s a claim on an engaged
          lead, and the Claim button still disappears. Only the label is gone, so an agent now
          learns the race is decided by the button's absence rather than by a badge. If
          duplicate owner calls start showing up, this is the removal to revisit. */}
      <LeadContactDensity c={g.__lead.contact} />
      {/* owner 2026-09-23: "how we know it's submitted for evaluation" — the lead's source_kind says it */}
      {g.__lead.source_kind === 'public_evaluation' && (
        <span className={`${COUNTER} bg-sky-500/15 text-sky-600`} title={t('desk_lead_evaluation_hint')}>
          {t('desk_lead_evaluation')}
        </span>
      )}
      {/* core 3706 (owner 2026-10-20): a watched agent's exclusive — "we save it with the agent's badge in leads".
          The name comes from public.watched_agent via /api/core/leads; the server drops it once the advert is gone
          or an owner's advert of the flat turns up. */}
      {g.__lead.watched_agent?.name && (
        <span className={`${COUNTER} bg-amber-500/15 text-amber-700`} title={t('desk_lead_watched_agent_hint')}>
          {tLog('desk_lead_watched_agent', { name: g.__lead.watched_agent.name })}
        </span>
      )}
      {/* B115: a lead whose last cooperation died comes back to the pool with a
          cooldown. SHOWN, not hidden (same ruling as `engaged`) — a badge saying
          why and until when tells the agent something; hiding teaches nothing.
          The server 409s the claim regardless, so this is the honest label on an
          enforced rule, not the rule itself. */}
      {/* RULING B: the cooldown is a ~1 hour debounce, so a DATE would be
          nonsense — show the clock time it lifts. RULING "show why, don't
          invent": the reason is rendered only when one was actually recorded. */}
      {cooling(g.__lead) && (
        <span className={`${COUNTER} bg-slate-500/15 text-slate-500`}>
          {tLog('desk_lead_cooldown', { d: new Date(g.__lead.debounce_until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}
          {g.__lead.debounce_reason ? ` · ${tOr('desk_coop_end_' + g.__lead.debounce_reason, g.__lead.debounce_reason)}` : ''}
        </span>
      )}
      {/* An agent's report that SOMEONE ELSE sold it is a claim, not a fact
          (owner ruling): it never removes the lead, it lowers its priority and
          says how many agents have reported it. The count is shown rather than a
          verdict, because the archiving threshold is deliberately unset until
          there is data to choose one from. */}
      {(g.__lead.exclusive_until || g.__lead.exclusive_until_sold) && (
        <span className={`${COUNTER} bg-violet-500/15 text-violet-600`}>
          {g.__lead.exclusive_until
            ? tLog('desk_lead_exclusive_until', { d: g.__lead.exclusive_until })
            : t('desk_lead_exclusive_until_sold')}
        </span>
      )}
      {goneOf(g) > 0 && (
        <span className={`${COUNTER} bg-amber-500/15 text-amber-600`}
          title={t('desk_lead_reported_hint')}>
          {tLog('desk_lead_reported_gone', { n: goneOf(g) })}
        </span>
      )}
      {g.__lead.status === 'dead' && (
        <span className={`${COUNTER} bg-slate-500/15 text-slate-500`}>
          {g.__lead.dead_reason ? tOr('desk_coop_end_' + g.__lead.dead_reason, g.__lead.dead_reason) : t('desk_lead_archive')}
        </span>
      )}
      {/* 0205: the "ამ ლიდზე მუშაობა" button is GONE. `working` is no longer
          something an agent declares — it is derived from work artifacts
          (cooperation / agent-recorded restriction / meeting). Do not add a
          button back here: a self-declared badge spends every colleague's
          restraint on evidence of nothing, which is what broke RULING 5.
          Private interest has its own control — the ♥ on the card. */}
    </div>} />
  </div>
  )

  return (
    // 0374 — the vote lives four components down, inside the contact popover; the context
    // carries the acting identity instead of threading a prop through HistoryGroup.
    // Leads is where this matters most: 0204 measured 52.8% of korter leads and 46.9% of
    // myhome leads with a phone as authored by a known agency agent, and the ones the
    // roster CANNOT see are exactly what this question is for.
    <ViewerCtx.Provider value={viewerCtx}>
    <AdTextCtx.Provider value={adTexts}>
    <div>
      {/* valuations started on sentavus.com and not sent (owner 2026-10-07) — closed by default, absent when empty */}
      {!archive && <UnsentValuations districts={districts} />}
      {/* Archive banner only — the active/archive TOGGLE lives in the search panel's
          Category tree now. While viewing the archive, one line says so + offers the way
          back (the panel may be collapsed; the state must never be invisible + unescapable). */}
      {/* Ownership LENS (SEARCH_GRAMMAR §3b). Sits beside Archive but is a DIFFERENT axis:
          Archive is lifecycle (what the lead is doing), this is the agent↔lead relationship
          (whose it is). They are adjacent because both scope the feed, never merged because
          merging them is what produced the "Leads/Mine/All" model the owner rejected.
          Hidden entirely when the parent supplies no handler — a control that cannot change
          anything is worse than no control. */}
      {/* 🔴 REMOVED 2026-08-02 (owner: "these pills are redundant") — the `All leads |
          Working` pill row that used to sit here.

          Redundant, because the panel now asks the scoping question properly: the pool
          selector chooses the inventory and the views row beside it carries Active/Archive.
          A second scoping control floating above the grid restated part of that, in a
          different shape, with no counts. §9e had already recorded the placement as wrong —
          "the Working lens was built inside the feed, while every other scoping control
          lives in the panel; placement was chosen for build convenience, not meaning."

          And WRONG, independently of placement. It filtered `lead_claim` — 3 rows, whose
          write gesture 0205 deliberately deleted — while calling it "Working", a word this
          system already uses for `property_lead.status='working'`, derived from real work
          artifacts (cooperation, restriction, meeting, repost, contact). 0205 exists
          precisely to stop a claim being displayed as work: "a self-declared badge spends
          every colleague's restraint on evidence of nothing."

          The lens returns when it is pointed at evidence (DOMAIN_VOCABULARY §5 R-1: the
          per-agent source is the `represents_property` edge those five triggers already
          write), and it returns IN THE PANEL beside the other views. If claims are ever
          shown, the control is called `Claimed` and needs its gesture built first.
          `onMineChange`/`mine` stay on the props for that return. */}
      {archive && (
        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
          <span>{t('desk_lead_archive')}</span>
          <button className="text-primary hover:underline" onClick={() => onArchiveChange(false)}>{t('desk_lead_active')} →</button>
        </div>
      )}
      {/* The "what earns the მუშაობს badge" hint (0205) was removed 2026-07-28 (owner):
          the badge rule has been live long enough that the explanation had outlived the
          transition it was written for. desk_lead_working_hint went with it. */}
      {err &&<p className="text-sm text-destructive"><Icon name="alert-triangle" /> {err}</p>}
      {/* Transport failure — one quiet line WITH a way out, and never a substitute for the
          rows: whatever is already on screen stays. The banner it replaces quoted the
          browser's raw fetch rejection ("Load failed" on Safari) and could only be cleared
          by changing the query. */}
      {loadFailed && (
        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="alert-triangle" className="text-amber-500" />
          {/* Existing app-wide strings, not new lead-specific ones — this is the same
              failure every tab can have, and it should read the same everywhere. */}
          <span>{t('app_tab_load_failed')}</span>
          <button className="text-primary hover:underline"
            onClick={() => { loadFailsRef.current = 0; setLoadFailed(false); load() }}>
            {t('app_error_retry')}
          </button>
        </div>
      )}
      {/* First load = the property feed's shimmer grid (design skin §4), not a text line.
          Only when there is nothing to show — an SWR revalidate happens behind real cards. */}
      {!leads && !loadFailed && (
        <div className={`grid gap-3 items-start ${LEAD_GRID(view)}`}>
          {Array.from({ length: 6 }, (_, i) => <SkeletonCard key={i} />)}
        </div>
      )}
      {leads?.length === 0 && <p className="text-sm text-muted-foreground">{t('desk_leads_empty')}</p>}
      {/* ⚠️ B1198: a page whose rows are not in the window yet is LOADING, not empty. Without
          the pageInWindow guard a jump prints "nothing matches your filters" over a set that
          matches thousands -- the feed telling the agent their search failed while it fetches
          the answer. Zero results is a claim; make it only when the rows are actually here. */}
      {leads?.length > 0 && shown.length === 0 && pageInWindow && (
        <p className="text-sm text-muted-foreground">{t('desk_empty_no_match_filters')}</p>
      )}
      {leads?.length > 0 && !pageInWindow && (
        <p className="text-sm text-muted-foreground">{t('desk_loading')}</p>
      )}
      {/* How many the search actually found — see ResultCount.jsx for the three rules.
          `total` is server truth for THIS query (set from the page-0 response, same value
          the sidebar rail reads via leadsCachedTotal), so it needs no fetch of its own.
          ⚠️ Gated on leads?.length, NOT on `total` being non-null: `total` initialises to
          0, not null, so passing it raw would print "0 ლიდი" over the skeleton on every
          first load. Zero results are the empty state's job, one line above. */}
      <ResultCount unit="leads" possible={shelfTotal}
        total={leads?.length ? total : null} />
      {/* View parity with Properties (owner 2026-07-24): MAP renders all lead pins via the
          shared EntriesMapView; GALLERY widens the cards + swaps the hero to large photos;
          CARD is the default thumb grid. Same HistoryGroup card in every case. */}
      {view === 'map' && leads?.length > 0 ? (
        <div className="rounded-lg overflow-hidden border border-border">
          <EntriesMapView groups={groups} districts={districts} features={features}
            corpusPins={corpusPins} onPinClick={onPinClick}
            onCardClick={onCardClick} expanded={mapExpanded} onToggleExpand={onToggleMapExpand} />
        </div>
      ) : (<>
      <div className={`grid gap-3 items-start ${LEAD_GRID(view)}`}>
        {/* a card just reported sold / rented drops below the rest of its page at once (stable: the others keep their order) */}
        {(goneLocal.size ? [...shown].sort((a, b) => (goneLocal.has(a.items?.[0]?.listing?.property_id) ? 1 : 0) - (goneLocal.has(b.items?.[0]?.listing?.property_id) ? 1 : 0)) : shown).map(renderLeadCard)}
      </div>
      {/* ⚠️ THIS USED TO BE A HAND-KEPT COPY — its own comment read "the property feed's pager,
          VERBATIM (owner: same exact system)" — and that is exactly why it drifted the moment
          the property feed gained clickable page numbers. It is the same COMPONENT now, so
          "same exact system" is enforced rather than promised.
          Server chunks (0187 cluster cursor) prefetch ahead via the effect above, so Next is
          normally never blocked on the network. */}
      {groups.length > PAGE_SIZE && (
        <FeedPager page={clampedPage} totalPages={totalPages} onPage={setPage} showJump />
      )}
      {rows && cursor < total && (
        <p className="mt-2 text-center text-xs text-muted-foreground">
          {loadingMore ? tLog('desk_load_more_properties', { loaded: groups.length, total }) : null}
        </p>
      )}
      {/* The possible-matches shelf, at last (2026-07-29). Same component, same header and
          same pager as the property feed — and the same card as the matched grid above.
          ⚠️ `serverGroups` and `serverTotal` are both CLUSTER counts from this cursor, so
          they compare like-for-like. This feed shelves nothing locally (its whole pipeline
          is server-side), so unlike IntakeTab there is no local tier to add on top. */}
      <PossibleMatchesShelf
        groups={shelfGroups} serverGroups={shelfGroups.length} serverTotal={shelfTotal}
        view={view} onLoadMore={loadMoreShelf} loading={shelfLoading}
        exhausted={shelfDoneRef.current} renderGroup={renderLeadCard} />
      </>)}
    </div>
    </AdTextCtx.Provider>
    </ViewerCtx.Provider>
  )
}

